import CustomButton from '@/components/atoms/CustomButton';
import svgIcons from '@/assets/svg/icons';
import { TContentType } from '@/types/Content.types';
import { HStack, Text, VStack } from '@chakra-ui/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  hasDetailsError,
  keepsExistingRemoteVideo,
  validateContentStep,
  validateDetails,
  validateForSubmit,
} from '@/modules/content/creation/model/validation';
import {
  TContentFormErrors,
  TContentFormMode,
  TContentFormSubmit,
  TContentFormValues,
  WizardFinalAction,
} from '@/modules/content/creation/model/types';
import { useContentForm } from '@/modules/content/creation/model/useContentForm';
import { getContentTypeLabel } from '@/modules/content/common/contentLabels';
import ArticleSectionsEditor from '@/modules/content/creation/steps/ArticleSectionsEditor';
import { hasArticleFieldError } from '@/modules/content/creation/model/articleValidation';
import DetailsStep from '@/modules/content/creation/steps/DetailsStep';
import PreviewStep from '@/modules/content/creation/steps/PreviewStep';
import { text } from '@/theme/colors/palettes';

export type { TContentFormMode } from '@/modules/content/creation/model/types';

// Articles get an extra read-only "arrange" step (manage images / sections) between
// editing and preview; posts go straight to preview.
type WizardStep = 'details' | 'content' | 'arrange' | 'preview';

// The content step is labelled with the content type itself (Post / Article / Reel).
const stepTitle = (step: WizardStep, type: TContentType): string => {
  switch (step) {
    case 'details':
      return 'Add Thumbnail and Details';
    case 'content':
      return getContentTypeLabel(type);
    case 'arrange':
      return 'Arrange';
    case 'preview':
      return 'Content Preview';
  }
};

type Props = {
  type: TContentType;
  authorLabel: string;
  defaultValues?: Partial<TContentFormValues>;
  // Existing thumbnail URL (edit mode), shown until a new file is picked.
  defaultThumbnailUrl?: string | null;
  allowOrganizationVisibility?: boolean;
  isSubmitting: boolean;
  // "Go Back" from the first step (e.g. back to the list).
  onExit: () => void;
  onSubmit: (mode: TContentFormMode, data: TContentFormSubmit) => void;
  // Terminal action on the Preview step (e.g. "Publish Content").
  primaryAction: WizardFinalAction;
  // Optional action on the Content step to save without previewing (e.g. "Save draft").
  secondaryAction?: WizardFinalAction;
  // Edit flows should not advance until the creator has changed authored data.
  requireChangesToContinue?: boolean;
  isVideoSourceReadOnly?: boolean;
};

const editableSnapshot = (values: TContentFormValues) => ({
  title: values.title,
  description: values.description,
  domains: values.domains,
  subdomains: values.subdomains,
  isPublic: values.isPublic,
  body: values.body,
  sections: values.sections,
  videoSource: values.videoSource,
  videoFile: values.videoFile,
  youtubeUrl: values.youtubeUrl,
  fullDescription: values.fullDescription,
});

const ContentWizard = ({
  type,
  authorLabel,
  defaultValues,
  defaultThumbnailUrl,
  allowOrganizationVisibility = true,
  isSubmitting,
  onExit,
  onSubmit,
  primaryAction,
  secondaryAction,
  requireChangesToContinue = false,
  isVideoSourceReadOnly = false,
}: Props) => {
  const [step, setStep] = useState<WizardStep>('details');
  const [isVideoCompressing, setIsVideoCompressing] = useState(false);
  const [isYoutubeDurationChecking, setIsYoutubeDurationChecking] = useState(false);
  const {
    values,
    errors,
    setErrors,
    setField,
    setSections,
    setDomains,
    setSubdomains,
    thumbnailFile,
    setThumbnailFile,
  } = useContentForm(defaultValues);
  const initialEditableValues = useRef(JSON.stringify(editableSnapshot(values)));
  const hasAuthoredChanges = useMemo(
    () => Boolean(thumbnailFile) || JSON.stringify(editableSnapshot(values)) !== initialEditableValues.current,
    [thumbnailFile, values],
  );

  useEffect(() => {
    if (allowOrganizationVisibility || values.isPublic) return;

    setField('isPublic', true);
  }, [allowOrganizationVisibility, setField, values.isPublic]);

  const goToNextStepFromDetails = () => {
    const nextErrors = validateDetails(values, type);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) setStep(hasContentStep ? 'content' : 'preview');
  };

  // An error is only visible on the step that renders its field. Details errors belong to
  // step 1; section text is editable on the content step (the arrange step shows it
  // read-only, so it cannot fix a length breach). Anything else has no step that can
  // display it — surfaced inline on preview instead, so submit is never blocked silently.
  const stepForErrors = (nextErrors: TContentFormErrors): WizardStep | undefined => {
    if (hasDetailsError(nextErrors)) return 'details';
    if (nextErrors.sections || hasArticleFieldError(nextErrors)) return 'content';
    return undefined;
  };

  const submit = (mode: TContentFormMode) => {
    const nextErrors = validateForSubmit(values, type, mode);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      const targetStep = stepForErrors(nextErrors);
      if (targetStep) setStep(targetStep);
      return;
    }
    onSubmit(mode, { values, thumbnailFile });
  };

  const goToNextStepFromContent = () => {
    const nextErrors = validateContentStep(values, type);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) setStep(afterContentStep);
  };

  // Errors with no editable step (e.g. a stored value a tightened limit now rejects).
  const unroutableError =
    Object.keys(errors).length > 0 && !stepForErrors(errors) ? Object.values(errors)[0] : undefined;

  const actionButton = (action: WizardFinalAction) => (
    <CustomButton
      key={action.key}
      label={action.label}
      backgroundColor={action.backgroundColor}
      isTransparent={action.isTransparent}
      labelColor={action.isTransparent ? 'text.mediumBlue' : 'extra.white'}
      labelHoverColor={action.isTransparent ? 'extra.black' : undefined}
      isLoading={isSubmitting}
      onClick={() => submit(action.mode)}
      width={'295px'}
    />
  );

  const continueButton = (onClick: () => void, disabled = false) => (
    <CustomButton
      label="Continue"
      backgroundColor="secondary.500"
      onClick={onClick}
      width={'295px'}
      labelColor="white"
      disabled={disabled}
    />
  );

  const goBackButton = (onClick: () => void) => (
    <CustomButton label="Go Back" isTransparent labelColor="background.blueGray" onClick={onClick} />
  );

  const contentStepByType: Partial<Record<TContentType, React.ReactNode>> = {
    article: (
      <ArticleSectionsEditor
        mode="edit"
        sections={values.sections}
        onChange={setSections}
        error={errors.sections}
        fieldErrors={errors}
      />
    ),
  };

  // Only articles have a content step. A post is a single block of text written as the
  // description on the details step, and a video's source is set there too.
  const hasContentStep = type === 'article';
  // After editing, articles go to the read-only "arrange" step.
  const afterContentStep: WizardStep = type === 'article' ? 'arrange' : 'preview';
  // The step immediately before preview — where "Go Back" from preview returns to.
  const beforePreviewStep: WizardStep = type === 'article' ? 'arrange' : 'details';

  const isMissingUploadedVideo =
    type === 'video' && values.videoSource === 'mux' && !values.videoFile && values.muxStatus === 'errored';
  const keepsExistingVideo = type === 'video' && keepsExistingRemoteVideo(values);
  const isDetailsContinueDisabled =
    (requireChangesToContinue && !hasAuthoredChanges) ||
    (type === 'video' &&
      !keepsExistingVideo &&
      ((values.videoSource === 'mux' && !values.videoFile) ||
        (values.videoSource === 'youtube' && !values.youtubeUrl) ||
        isVideoCompressing ||
        isYoutubeDurationChecking ||
        Boolean(values.videoProcessingError) ||
        isMissingUploadedVideo));

  return (
    <VStack align="center" width="100%" gap="24px" marginBottom="48px">
      {/* Top bar: back to the list (left) + save-as-draft (right), available on every step */}
      <HStack width="100%" justify="space-between" align="center">
        <CustomButton
          isTransparent
          iconBeforeLabelSvgPath={svgIcons.leftArrow}
          iconBeforeLabelSvgBoxHeight={12}
          iconBeforeLabelSvgColor={text.mediumBlue}
          labelColor="text.mediumBlue"
          labelHoverColor="extra.black"
          label="Back to Your Content"
          onClick={onExit}
        />
        {secondaryAction && actionButton(secondaryAction)}
      </HStack>

      <Text variant={'loraMediumTitle'} color={'extra.black'} fontStyle={'italic'} fontSize={'28px'} mb={'10px'}>
        {stepTitle(step, type)}
      </Text>

      {step === 'details' && (
        <>
          <DetailsStep
            type={type}
            isVideoSourceReadOnly={isVideoSourceReadOnly}
            values={values}
            errors={errors}
            authorLabel={authorLabel}
            thumbnailFile={thumbnailFile}
            defaultThumbnailUrl={defaultThumbnailUrl}
            allowOrganizationVisibility={allowOrganizationVisibility}
            onField={setField}
            onDomainsChange={setDomains}
            onSubdomainsChange={setSubdomains}
            onThumbnailChange={setThumbnailFile}
            onVideoCompressingChange={setIsVideoCompressing}
            onYoutubeDurationCheckingChange={setIsYoutubeDurationChecking}
          />
          <VStack gap="10px">{continueButton(goToNextStepFromDetails, isDetailsContinueDisabled)}</VStack>
        </>
      )}

      {step === 'content' && (
        <>
          {contentStepByType[type]}
          <VStack gap="10px">
            {continueButton(goToNextStepFromContent)}
            {goBackButton(() => setStep('details'))}
          </VStack>
        </>
      )}

      {step === 'arrange' && (
        <>
          <ArticleSectionsEditor
            mode="arrange"
            sections={values.sections}
            onChange={setSections}
            error={errors.sections}
          />
          <VStack gap="10px">
            {continueButton(() => setStep('preview'))}
            {goBackButton(() => setStep('content'))}
          </VStack>
        </>
      )}

      {step === 'preview' && (
        <>
          <PreviewStep
            type={type}
            values={values}
            authorLabel={authorLabel}
            thumbnailFile={thumbnailFile}
            defaultThumbnailUrl={defaultThumbnailUrl}
          />
          {unroutableError && (
            <Text color="extra.red" fontSize="13px" textAlign="center">
              {unroutableError}
            </Text>
          )}
          <VStack gap="10px">
            {actionButton(primaryAction)}
            {goBackButton(() => setStep(beforePreviewStep))}
          </VStack>
        </>
      )}
    </VStack>
  );
};

export default ContentWizard;
